import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {downloadBudgetReport} from "../../utils/requestUtils";
import Config from "../../core/config";
import {formatDateString} from "../../utils/utils";
import Loading from "../../core/Loading";

const apiUrl = Config.apiUrl;

const RentalViewer = ({id, isBudget, onClose}) => {
    const [rental, setRental] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingPdf, setGeneratingPdf] = useState(false);

    useEffect(() => {
        loadRental();
    }, [id]);

    async function loadRental() {
        try {
            setLoading(true);
            const url = apiUrl + '/api/rentals/id=' + id;
            const response = await makeRequest(url, 'GET');
            setRental(response);
        } catch (error) {
            console.error('Error loading rental:', error);
            alert('Erro ao carregar dados do ' + (isBudget ? 'orçamento' : 'aluguel'));
        } finally {
            setLoading(false);
        }
    }

    async function handleGeneratePdf() {
        if (generatingPdf || !rental) {
            return;
        }

        try {
            setGeneratingPdf(true);
            await downloadBudgetReport(rental.id, rental.code);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Erro ao gerar o PDF do orçamento. Tente novamente.');
        } finally {
            setGeneratingPdf(false);
        }
    }

    if (loading) {
        return <Loading />;
    }

    if (!rental) {
        return <div>Erro ao carregar dados</div>;
    }

    return (
        <div className="app-form-page" style={{padding: '20px', margin: '0 auto'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <div className="header-label">
                    Visualizar {isBudget ? 'Orçamento' : 'Aluguel'} - {rental.code}
                </div>
                {isBudget && (
                    <button onClick={handleGeneratePdf} disabled={generatingPdf}>
                        {generatingPdf ? 'Gerando...' : 'Gerar PDF'}
                    </button>
                )}
            </div>

            <div>
                {isBudget && (
                    <div style={{textAlign: 'center', marginBottom: '20px'}}>
                        <h2 style={{margin: 0, fontSize: '24px'}}>Orçamento</h2>
                    </div>
                )}

                {/* Informações do Cliente */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Cliente</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                        <div>
                            <strong>Nome:</strong> {rental.customer?.name}
                        </div>
                        <div>
                            <strong>Telefone:</strong> {rental.customer?.phone}
                        </div>
                        <div>
                            <strong>Email:</strong> {rental.customer?.email}
                        </div>
                        <div>
                            <strong>CPF/CNPJ:</strong> {rental.customer?.cpfCnpj}
                        </div>
                    </div>
                </div>

                {/* Informações do Aluguel/Orçamento */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Informações Gerais</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                        <div>
                            <strong>Data:</strong> {formatDateString(rental.rentalDate)}
                        </div>
                    </div>
                </div>

                {/* Endereço */}
                {rental.address && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Endereço de Entrega</h3>
                        <div>
                            {rental.address}
                            {rental.addressNumber && `, ${rental.addressNumber}`}
                            {rental.addressComplement && ` - ${rental.addressComplement}`}
                            {rental.neighborhood && ` - ${rental.neighborhood}`}
                            {rental.city && `, ${rental.city}`}
                            {rental.state && ` - ${rental.state}`}
                            {rental.cep && `, CEP: ${rental.cep}`}
                        </div>
                    </div>
                )}

                {/* Itens */}
                <div style={{marginBottom: '20px'}}>
                    <h3>Itens</h3>
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                        <thead>
                            <tr style={{backgroundColor: '#f5f5f5'}}>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Produto</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Quantidade</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Dias</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Data Entrega</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Data Devolução</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Valor Unit.</th>
                                <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rental.rentalItems?.map((item, index) => (
                                <tr key={index}>
                                    <td style={{border: '1px solid #ddd', padding: '8px'}}>{item.product?.name}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{item.quantity}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{item.rentalDays}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(item.rentalDate)}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(item.returnDate)}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {item.value?.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</td>
                                    <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {item.total?.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Valores */}
                <div style={{marginBottom: '20px'}}>
                    <div style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                        <div>
                            <strong>Valor (Itens):</strong> R$ {(rental.totalValue - (rental.shippingCost || 0))?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                        <div>
                            <strong>Frete:</strong> R$ {(rental.shippingCost || 0)?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                        <div style={{borderLeft: '1px solid #ddd', paddingLeft: '20px'}}>
                            <strong>Valor Total:</strong> R$ {rental.totalValue?.toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </div>
                    </div>
                </div>

                {/* Formas de Pagamento */}
                {rental.paymentMethods && rental.paymentMethods.length > 0 && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Formas de Pagamento</h3>
                        <table style={{width: '100%', borderCollapse: 'collapse'}}>
                            <thead>
                                <tr style={{backgroundColor: '#f5f5f5'}}>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>Forma de Pagamento</th>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>Valor</th>
                                    <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>Vencimento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rental.paymentMethods.map((payment, index) => (
                                    <tr key={index}>
                                        <td style={{border: '1px solid #ddd', padding: '8px'}}>{payment.paymentMethod?.name}</td>
                                        <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'right'}}>R$ {payment.value?.toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}</td>
                                        <td style={{border: '1px solid #ddd', padding: '8px', textAlign: 'center'}}>{formatDateString(payment.dueDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Observações */}
                {rental.details && (
                    <div style={{marginBottom: '20px'}}>
                        <h3>Observações</h3>
                        <div style={{padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '4px'}}>
                            {rental.details}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RentalViewer; 